import { cn } from '@/lib/utils';

/** Preset keywords mapped to CSS `mask-position`. */
export type MaskPositionPreset = 'center' | 'top' | 'right' | 'bottom' | 'left';

export type MaskPosition = MaskPositionPreset;

const MASK_POSITION_MAP: Record<MaskPositionPreset, string> = {
    center: 'center',
    top: 'center top',
    right: 'right center',
    bottom: 'center bottom',
    left: 'left center',
};

function resolveMaskPosition(position: string): string {
    if (Object.prototype.hasOwnProperty.call(MASK_POSITION_MAP, position)) {
        return MASK_POSITION_MAP[position as MaskPositionPreset];
    }
    return position;
}

/** Preset or any valid CSS `mask-position` (e.g. `center 10%`, `25% center`). */
export type MaskPositionValue = MaskPositionPreset | (string & {});

export type DecorativeSvgMaskProps = {
    /** Resolved URL from `import x from '*.svg'` (Vite). */
    src: string;
    /** Tailwind background classes applied behind the mask (e.g. theme tokens). */
    maskFillClassName: string;
    /** Opacity of the masked graphic (default 20%). */
    opacityClassName?: string;
    /** Passed to `mask-size` / `-webkit-mask-size`. */
    maskSize?: string;
    /** Preset (`top`, `left`, …) or raw CSS `mask-position` (e.g. `center 10%`). Default `center`. */
    maskPosition?: MaskPositionValue;
    /**
     * When true, inline styles omit `mask-size` and `mask-position` so `className` (or a stylesheet)
     * can define fluid geometry (`clamp`, `vw`, media queries) without inline overrides.
     */
    deferGeometryToClassName?: boolean;
    className?: string;
};

/**
 * Renders an SVG as a centered CSS mask so fill color follows theme tokens.
 * Parent should be `relative` (and usually `overflow-hidden` if clipped to rounded corners).
 *
 * `maskSize` quick reference (CSS `mask-size`: width, then height):
 * - `88% auto` — width-led; tall wide cards get vertical overflow/clipping (e.g. Inspire callout). Tune ~82–90%.
 * - Increase first value → wider graphic, more vertical bleed/clipping.
 * - Decrease first value → narrower, less clipping.
 * - `auto 70%` — height-led; keeps graphic inside the box (default below).
 *
 * `maskPosition`: default `center`. Presets: `center`, `top`, `right`, `bottom`, `left`. Or pass any CSS value, e.g. `center 10%`, `25% center`.
 */
export function DecorativeSvgMask({
    src,
    maskFillClassName,
    opacityClassName = 'opacity-20',
    maskSize = 'auto 65%',
    maskPosition = 'center',
    deferGeometryToClassName = false,
    className,
}: DecorativeSvgMaskProps) {
    const url = `url("${src}")`;
    const cssMaskPosition = resolveMaskPosition(maskPosition);
    const geometryStyle = deferGeometryToClassName
        ? {}
        : {
                maskSize,
                maskPosition: cssMaskPosition,
                WebkitMaskSize: maskSize,
                WebkitMaskPosition: cssMaskPosition,
            };
    return (
        <div
            className={cn(
                'pointer-events-none absolute inset-0',
                opacityClassName,
                maskFillClassName,
                className,
            )}
            style={{
                maskImage: url,
                maskRepeat: 'no-repeat',
                ...geometryStyle,
                WebkitMaskImage: url,
                WebkitMaskRepeat: 'no-repeat',
            }}
            aria-hidden
        />
    );
}
